import {
  Button,
  TextField,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  ListItemText,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNewKit } from "../useKitQueries";
import { useGetPatients } from "../../PatientComponents";
import { useGetOrganizations } from "../../HandlingComponents";
import { useGetDevices } from "../../DeviceComponents";
import { prettify, renderDeviceName } from "../../../utils/utils";
import { isDeviceEligibleForKit } from "../kitDeviceUtils";

const validationSchema = yup.object({
  name: yup.string().required("Nome obbligatorio"),
  org_id: yup
    .number()
    .required("Organizzazione è un campo obbligatorio"),
  patient_id: yup.mixed().nullable(),
  device_ids: yup.array().of(yup.number()),
});

function AddKit({ closeModal }) {
  const newKitMutation = useNewKit(closeModal);
  const patientsQuery = useGetPatients();
  const organizationsQuery = useGetOrganizations();
  const devicesQuery = useGetDevices("all");

  const patients = patientsQuery.data?.patients ?? [];
  const organizations = organizationsQuery.data?.organizations ?? [];
  const allDevices = devicesQuery.data?.devices ?? [];

  const formik = useFormik({
    initialValues: {
      name: "",
      org_id: "",
      patient_id: "",
      device_ids: [],
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        org_id: Number(values.org_id),
        patient_id:
          values.patient_id === "" ? null : Number(values.patient_id),
      };
      if (values.device_ids.length > 0) {
        payload.device_ids = values.device_ids.map(Number);
      }
      newKitMutation.mutate(payload);
    },
  });

  const eligibleDevices = formik.values.org_id
    ? allDevices.filter((d) =>
        isDeviceEligibleForKit(d, formik.values.org_id)
      )
    : [];

  const filteredPatients = formik.values.org_id
    ? patients.filter(
        (p) => Number(p.org_id) === Number(formik.values.org_id)
      )
    : [];

  const handleOrgChange = (event) => {
    const orgId =
      event.target.value === "" ? "" : Number(event.target.value);
    formik.setFieldValue("org_id", orgId);
    formik.setFieldValue("device_ids", []);
    if (formik.values.patient_id !== "") {
      const patient = patients.find(
        (p) => p.id === Number(formik.values.patient_id)
      );
      if (patient && Number(patient.org_id) !== orgId) {
        formik.setFieldValue("patient_id", "");
      }
    }
  };

  const handleDeviceIdsChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue(
      "device_ids",
      typeof value === "string" ? value.split(",").map(Number) : value
    );
  };

  const renderSelectedDevices = (selected) =>
    selected
      .map((id) => {
        const d = allDevices.find((el) => Number(el.id) === Number(id));
        return d ? prettify(d.name) : id;
      })
      .join(", ");

  const handlePatientChange = (event) => {
    const patientId =
      event.target.value === "" ? "" : Number(event.target.value);
    formik.setFieldValue("patient_id", patientId);

    if (patientId !== "" && formik.values.org_id === "") {
      const patient = patients.find((p) => p.id === patientId);
      if (patient?.org_id != null) {
        formik.setFieldValue("org_id", patient.org_id);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Nuovo kit</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          id="name"
          name="name"
          label="Nome kit (*)"
          variant="standard"
          onChange={formik.handleChange}
          value={formik.values.name}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        {organizationsQuery.isSuccess && (
          <TextField
            select
            margin="dense"
            fullWidth
            id="org_id"
            name="org_id"
            label="Organizzazione (*)"
            variant="standard"
            onChange={handleOrgChange}
            value={formik.values.org_id}
            error={formik.touched.org_id && Boolean(formik.errors.org_id)}
            helperText={formik.touched.org_id && formik.errors.org_id}
          >
            <MenuItem value="">
              <em>Seleziona organizzazione</em>
            </MenuItem>
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          margin="dense"
          fullWidth
          id="patient_id"
          name="patient_id"
          label="Paziente"
          variant="standard"
          onChange={handlePatientChange}
          value={formik.values.patient_id}
          disabled={formik.values.org_id === ""}
        >
          <MenuItem value="">
            <em>Nessun paziente</em>
          </MenuItem>
          {filteredPatients.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {`${p.first_name} ${p.last_name}`}
            </MenuItem>
          ))}
        </TextField>

        {devicesQuery.isSuccess && (
          <>
            <TextField
              select
              margin="dense"
              fullWidth
              id="device_ids"
              name="device_ids"
              label="Strumenti iniziali"
              variant="standard"
              disabled={formik.values.org_id === ""}
              value={formik.values.device_ids}
              onChange={handleDeviceIdsChange}
              SelectProps={{
                multiple: true,
                renderValue: renderSelectedDevices,
              }}
              helperText={
                formik.values.org_id === ""
                  ? "Seleziona prima l'organizzazione"
                  : eligibleDevices.length === 0
                    ? "Nessuno strumento disponibile (stessa org, senza kit e senza paziente)"
                    : "Solo strumenti liberi della stessa organizzazione"
              }
            >
              {eligibleDevices.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  <Checkbox
                    checked={formik.values.device_ids.includes(d.id)}
                  />
                  <ListItemText
                    primary={prettify(d.name)}
                    secondary={renderDeviceName(d.device_type)}
                  />
                </MenuItem>
              ))}
            </TextField>
            {formik.values.device_ids.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {formik.values.device_ids.length} strumento/i selezionato/i
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={newKitMutation.isLoading} type="submit">
          Crea
        </Button>
        <Button
          disabled={newKitMutation.isLoading}
          color="error"
          onClick={closeModal}
        >
          Annulla
        </Button>
      </DialogActions>
    </form>
  );
}

export default AddKit;
